import React, { useEffect, useState } from 'react';

import {
  CloseOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  StarFilled,
  StarOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { SavedFilterDTO } from '@recurrency/core-api-schema/dist/savedFilters/common';
import { Menu, notification } from 'antd';
import { theme } from 'theme';

import { Button } from 'components/Button';
import { Counter } from 'components/Counter';
import { Dropdown } from 'components/Dropdown';
import { FlexSpace } from 'components/FlexSpace';
import { Input } from 'components/Input';
import { Modal } from 'components/Modal';
import { ConfirmModal } from 'components/Modal/ConfirmModal';
import { Tooltip } from 'components/Tooltip';

import { useCoreApi } from 'hooks/useApi';

import { coreApiFetch } from 'utils/api';
import { showAsyncModal } from 'utils/asyncModal';
import { captureAndShowError } from 'utils/error';
import { isEqual } from 'utils/object';
import { useHashState } from 'utils/routes';

import { SearchFrameHashState } from 'types/hash-state';

import { getSavedFilterData } from '../utils';

// Keep in sync with MAX_SAVED_FILTERS in core-api (repositories/savedFilter/postgres.ts).
const MAX_SAVED_FILTERS = 10;

// Matches the facet filter buttons (AsyncMultiSelect): neutral border that turns primary on hover
// with the text color left unchanged.
const filterButtonClassName = css`
  &.ant-btn {
    border-color: ${theme.colors.neutral[300]};
  }

  /* Don't show over/focus changes if button disabled */
  &.ant-btn:not([disabled]):focus,
  &.ant-btn:not([disabled]):hover {
    border-color: ${theme.colors.primary[500]};
    color: inherit;
  }
`;

const filterButtonIconClassName = cx(
  filterButtonClassName,
  css`
    &.ant-btn {
      padding-left: 8px;
      padding-right: 8px;
    }
  `,
);

type SaveModalState = { mode: 'closed' } | { mode: 'new' } | { mode: 'rename'; filter: SavedFilterDTO };

export function PlanningSavedFilters({ clearSelection }: { clearSelection: () => void }) {
  const [hashState, updateHashState] = useHashState<SearchFrameHashState>();
  const [activeFilterId, setActiveFilterId] = useState<string | undefined>();
  const [modal, setModal] = useState<SaveModalState>({ mode: 'closed' });
  const [nameInput, setNameInput] = useState('');
  // True while any write (create / rename / save-dirty) is in flight — disables the save controls.
  const [isSaving, setIsSaving] = useState(false);

  const { data, reload, setData } = useCoreApi(schemas.savedFilters.getSavedFilters, {});
  const filters = data?.items ?? [];
  const activeFilter = filters.find((filter) => filter.id === activeFilterId);

  const currentFilterData = getSavedFilterData(hashState);
  const isDirty = !!activeFilter && !isEqual(currentFilterData, getSavedFilterData(activeFilter.data));
  const isSavableState = !!(currentFilterData.where || currentFilterData.query || currentFilterData.extFilter);
  const atFilterLimit = filters.length >= MAX_SAVED_FILTERS;

  // The "+" button creates a new filter from the current state (independent of any active filter);
  // the save button only saves changes onto the active filter.
  const canSaveDirty = !!activeFilter && isDirty;
  const canCreateNew = isSavableState && !atFilterLimit;
  const createButtonTooltip = atFilterLimit
    ? `Filter limit reached (${MAX_SAVED_FILTERS})`
    : 'Save current filters as a new filter';

  // Once filters load, reflect the currently-applied filter (e.g. the default applied on open) in the trigger.
  useEffect(() => {
    if (activeFilterId || filters.length === 0) return;
    const match = filters.find((filter) => isEqual(currentFilterData, getSavedFilterData(filter.data)));
    if (match) setActiveFilterId(match.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Optimistically patch the saved filters list so the UI reflects mutations immediately; the caller then
  // fires the request and reloads in the background to reconcile with the server.
  const optimisticSetItems = (updater: (items: SavedFilterDTO[]) => SavedFilterDTO[]) => {
    if (data) setData({ ...data, items: updater(data.items) });
  };

  const applyFilter = (filter: SavedFilterDTO) => {
    updateHashState({
      where: undefined,
      extFilter: undefined,
      query: undefined,
      sortBy: undefined,
      secondarySortBy: undefined,
      ...getSavedFilterData(filter.data),
      page: 0,
    });
    setActiveFilterId(filter.id);
    clearSelection();
  };

  // Detach from the active saved filter without changing the applied hash state — the current
  // filters stay on screen, they're just no longer tied to a saved filter.
  const detachActiveFilter = () => {
    setActiveFilterId(undefined);
  };

  const createFilter = async (name: string) => {
    setIsSaving(true);
    try {
      const { data: created } = await coreApiFetch(schemas.savedFilters.createSavedFilter, {
        bodyParams: { name, isDefault: false, data: currentFilterData },
      });
      setActiveFilterId(created.id);
      await reload();
      setModal({ mode: 'closed' });
      notification.success({ message: `Saved filter "${created.name}" created` });
    } catch (err) {
      captureAndShowError(err, `Unable to save filter "${name}"`);
    } finally {
      setIsSaving(false);
    }
  };

  // Save the current filters onto the active saved filter. Disables immediately (guards against
  // double-clicks) and runs async — the optimistic update clears the dirty state right away.
  const saveDirtyToActiveFilter = () => {
    if (!activeFilter || isSaving) return;
    const { id: filterId, name: filterName } = activeFilter;
    const snapshot = currentFilterData;
    setIsSaving(true);
    optimisticSetItems((items) => items.map((f) => (f.id === filterId ? { ...f, data: snapshot } : f)));
    coreApiFetch(schemas.savedFilters.updateSavedFilter, {
      pathParams: { savedFilterId: filterId },
      bodyParams: { data: snapshot },
    })
      .then(() => notification.success({ message: `Filter "${filterName}" updated` }))
      .catch((err) => captureAndShowError(err, `Unable to update filter "${filterName}"`))
      .finally(() => {
        setIsSaving(false);
        reload();
      });
  };

  const renameFilter = async (filter: SavedFilterDTO, name: string) => {
    setIsSaving(true);
    try {
      await coreApiFetch(schemas.savedFilters.updateSavedFilter, {
        pathParams: { savedFilterId: filter.id },
        bodyParams: { name },
      });
      await reload();
      setModal({ mode: 'closed' });
    } catch (err) {
      captureAndShowError(err, `Unable to rename filter "${filter.name}"`);
    } finally {
      setIsSaving(false);
    }
  };

  // At most one default per user; the server clears the previous default when a new one is set. Runs
  // optimistically + async so the star updates instantly instead of waiting on two round-trips.
  const toggleDefault = (filter: SavedFilterDTO) => {
    const swappedIsDefault = !filter.isDefault;
    optimisticSetItems((items) =>
      items.map((f) => ({ ...f, isDefault: f.id === filter.id ? swappedIsDefault : false })),
    );
    coreApiFetch(schemas.savedFilters.updateSavedFilter, {
      pathParams: { savedFilterId: filter.id },
      bodyParams: { isDefault: swappedIsDefault },
    })
      .then(() => reload())
      .catch((err) => {
        captureAndShowError(err, `Unable to update default filter`);
        reload();
      });
  };

  const deleteFilter = async (filter: SavedFilterDTO) => {
    const didConfirm = await showAsyncModal(ConfirmModal, {
      title: `Delete saved filter "${filter.name}"?`,
      content: 'This cannot be undone.',
      okText: 'Delete',
      cancelText: 'Cancel',
      width: 480,
    });
    if (!didConfirm) return;
    if (activeFilterId === filter.id) setActiveFilterId(undefined);
    optimisticSetItems((items) => items.filter((f) => f.id !== filter.id));
    coreApiFetch(schemas.savedFilters.deleteSavedFilter, { pathParams: { savedFilterId: filter.id } })
      .then(() => notification.success({ message: `Deleted saved filter "${filter.name}"` }))
      .catch((err) => captureAndShowError(err, `Unable to delete filter "${filter.name}"`))
      .finally(() => reload());
  };

  const openNewModal = () => {
    setNameInput('');
    setModal({ mode: 'new' });
  };
  const openRenameModal = (filter: SavedFilterDTO) => {
    setNameInput(filter.name);
    setModal({ mode: 'rename', filter });
  };
  const onModalSubmit = () => {
    const name = nameInput.trim();
    if (name.length === 0) return;
    if (modal.mode === 'new') createFilter(name);
    else if (modal.mode === 'rename') renameFilter(modal.filter, name);
  };

  const triggerLabel = activeFilter ? `Current filter: ${activeFilter.name}` : 'Saved filters';

  const overlay = (
    <Menu>
      {filters.length === 0 && <Menu.Item disabled>No saved filters</Menu.Item>}
      {filters.map((filter) => (
        <Menu.Item key={filter.id} onClick={() => applyFilter(filter)}>
          <FlexSpace
            fullWidth
            justify="space-between"
            alignItems="center"
            gap={8}
            className={css`
              min-width: 240px;
            `}
          >
            <span
              className={css`
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              `}
            >
              {filter.name}
            </span>
            <FlexSpace gap={0} alignItems="center">
              <Tooltip title={filter.isDefault ? 'Default filter — click to unset' : 'Set as default filter'}>
                <Button
                  type="link"
                  size="small"
                  icon={
                    filter.isDefault ? <StarFilled style={{ color: theme.colors.primary[400] }} /> : <StarOutlined />
                  }
                  onClick={(ev) => {
                    ev.stopPropagation();
                    toggleDefault(filter);
                  }}
                />
              </Tooltip>
              <Tooltip title="Rename filter">
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    openRenameModal(filter);
                  }}
                />
              </Tooltip>
              <Tooltip title="Delete filter">
                <Button
                  type="link"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    deleteFilter(filter);
                  }}
                />
              </Tooltip>
            </FlexSpace>
          </FlexSpace>
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <FlexSpace alignItems="center" gap={8}>
      <Dropdown overlay={overlay}>
        <Button size="small" className={filterButtonClassName}>
          <div
            className={css`
              display: flex;
              align-items: center;
              gap: 6px;
            `}
          >
            <UnorderedListOutlined style={{ color: theme.colors.neutral[500] }} />
            {triggerLabel}
            <Counter value={filters.length} selected={filters.length > 0} variant="primary" />
            {isDirty && (
              <span
                className={css`
                  color: ${theme.colors.primary[500]};
                `}
              >
                · Unsaved changes
              </span>
            )}
            <DownOutlined style={{ color: theme.colors.neutral[400], fontSize: '12px' }} />
          </div>
        </Button>
      </Dropdown>
      <Tooltip title={createButtonTooltip}>
        <span>
          <Button size="small" className={filterButtonIconClassName} disabled={!canCreateNew} onClick={openNewModal}>
            <PlusOutlined />
          </Button>
        </span>
      </Tooltip>
      <Tooltip title="Save changes to this filter">
        <span>
          <Button
            size="small"
            className={filterButtonIconClassName}
            disabled={isSaving || !canSaveDirty}
            onClick={saveDirtyToActiveFilter}
          >
            <SaveOutlined />
          </Button>
        </span>
      </Tooltip>
      {activeFilter && (
        <Tooltip title="Unselect filter">
          <span>
            <Button size="small" className={filterButtonIconClassName} onClick={detachActiveFilter}>
              <CloseOutlined />
            </Button>
          </span>
        </Tooltip>
      )}
      <Modal
        visible={modal.mode !== 'closed'}
        destroyOnClose
        title={modal.mode === 'rename' ? 'Rename filter' : 'Save current filters as new'}
        onCancel={() => setModal({ mode: 'closed' })}
        onOk={onModalSubmit}
        okButtonProps={{ disabled: nameInput.trim().length === 0 }}
        confirmLoading={isSaving}
        width={480}
      >
        <div
          className={css`
            margin-bottom: 8px;
          `}
        >
          Filter name
        </div>
        <Input
          autoFocus
          value={nameInput}
          onChange={(ev) => setNameInput(ev.target.value)}
          onPressEnter={onModalSubmit}
        />
      </Modal>
    </FlexSpace>
  );
}
